import { forwardRef, useEffect, useMemo, useState } from "react";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "src/common/utils";
import { useMe } from "src/context/MeContext";
import { shouldHideUsageCredits } from "src/utils/brandTheme";

import { preprocessMarkdownContent, processMarkdownText } from "../chat/markdownProcessing";
import { MarkdownDagNodeButton } from "../MarkdownDagNodeButton";
import { ReferencedMemoriesSectionAsync } from "../ReferencedMemoriesSectionAsync";
import { MemoryReference,Source, SourceReference } from "../SourceReference";
import { SourcesSection } from "../SourcesSection";
import { TIME_MARKER, TimeMarker } from "../TimeMarker";
import type { MemoryAttribution, MemoryBody } from "../types/memory-types";
import { extractReferencedSourceIds } from "../utils";
import { isMdastTableNode, MarkdownTable } from "./markdown-table";
// Source utilities are used in the component code directly

export interface StyledMarkdownProps {
  children: string;
  className?: string;
  timeOnly?: boolean;
  variant?: "default" | "printable";
  dagNodes?: ReadonlyArray<{
    nodeId: string;
    title: string;
    status: string;
    sources?: ReadonlyArray<Source>;
  }> | null;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string) => void;
  // For MessageProcessor compatibility
  sources?: ReadonlyArray<Source>;
  activeToolId?: string | null;
  onToolClick?: (toolId: string) => void;
  usage?: {
    readonly billingTokens: number;
  } | null;
  copyButton?: React.ReactNode;
  showSourcesSection?: boolean;
  onRequestShowSources?: () => void;
  forceInlineArtifacts?: boolean;
  sourcesExpanded?: boolean;
  onSourcesExpandedChange?: (expanded: boolean) => void;
  memoryAttributions?: ReadonlyArray<MemoryAttribution>;
  conversationId?: string;
  messageId?: string;
  onEditMemory?: (memoryId: string, memoryBody: MemoryBody) => void;
  isV2?: boolean;  // If true, this is from LlmConversation/LlmMessage (V2)
}

export const Markdown = forwardRef<HTMLDivElement, StyledMarkdownProps>(
  ({
    children,
    className,
    timeOnly = false,
    variant = "default",
    dagNodes,
    selectedNodeId,
    onSelectNode,
    sources,
    usage,
    activeToolId,
    onToolClick,
    copyButton,
    showSourcesSection = true,
    onRequestShowSources,
    forceInlineArtifacts = false,
    sourcesExpanded,
    onSourcesExpandedChange,
    memoryAttributions,
    conversationId,
    messageId,
    onEditMemory,
    isV2,
  }, ref) => {
    const [activeSourceId, setActiveSourceId] = useState<string | null>(null);
    const [activeMemoryId, setActiveMemoryId] = useState<string | null>(null);
    const [preprocessedContent, setPreprocessedContent] = useState<string>("");
    const me = useMe();
    const hideUsage = shouldHideUsageCredits(me.organization?.brandTheme);

    // Preprocess markdown content to fix formatting issues
    useEffect(() => {
      if (typeof children === "string") {
        // Use shared preprocessing logic
        preprocessMarkdownContent(children).then(setPreprocessedContent);
      } else {
        setPreprocessedContent(children);
      }
    }, [children]);

    // Support both activeToolId (from MessageProcessor) and activeSourceId (native Markdown)
    const currentActiveId = activeToolId ? `tool_${activeToolId}` : activeSourceId;

    // Extract the set of referenced source IDs from the content
    const referencedSourceIds = useMemo(() => {
      if (typeof children !== "string") {
        return new Set<string>();
      }
      return extractReferencedSourceIds(children);
    }, [children]);

    // Create a mapping of source IDs to indices and collect all sources from dagNodes
    // Only include sources that are referenced in the content
    const { sourceIdToIndex, allSources } = useMemo(() => {
      const index: Record<string, number> = {};
      const sourcesArray: Array<Source> = [];

      let sourceIndex = 1;

      // Handle sources from MessageProcessor (tool references)
      if (sources && sources.length > 0) {
        sources.forEach((source) => {
          if (source.toolId) {
            const toolSourceId = `tool_${source.toolId}`;
            index[toolSourceId] = sourceIndex++;
            sourcesArray.push({
              ...source,
              sourceId: toolSourceId,
            });
          }
        });
      }

      // Handle dagNodes sources
      if (dagNodes) {
        dagNodes.forEach((node) => {
          if (!node.sources) return;

          node.sources.forEach((source) => {
            if (source.sourceId) {
              // Use format nodeId_sourceId for the full source reference
              const fullSourceId = `${node.nodeId}_${source.sourceId}`;

              // Only include this source if it's referenced in the content
              if (referencedSourceIds.has(fullSourceId)) {
                index[fullSourceId] = sourceIndex++;
                sourcesArray.push({
                  ...source,
                  // Ensure the source has the node prefix in its ID for lookups
                  sourceId: fullSourceId,
                });
              }
            }
          });
        });
      }

      return { sourceIdToIndex: index, allSources: sourcesArray };
    }, [dagNodes, referencedSourceIds, sources]);

    // Apply our custom processing for Slack-style links, step references, and source references
    const processedText = useMemo(() => {
      // Only process if preprocessedContent is actually a string
      if (typeof preprocessedContent !== "string" || !preprocessedContent.length) {
        // Return the original children if preprocessing hasn't completed yet
        return typeof children === "string" ? children : "";
      }

      // Use shared processing logic
      return processMarkdownText(preprocessedContent, sources || []);
    }, [preprocessedContent, sources, children]);

    const handleSourceClick = (sourceId: string) => {
      if (!showSourcesSection) {
        onRequestShowSources?.();
      }

      // If onToolClick is provided, use that for tool references
      if (sourceId.startsWith("tool_") && onToolClick) {
        const toolId = sourceId.replace("tool_", "");
        onToolClick(toolId);
        // Also set activeSourceId so the source is highlighted
        setActiveSourceId(sourceId);
      } else {
        setActiveSourceId(sourceId);
      }

      // Scroll to the source section for both cases
      setTimeout(() => {
        const element = document.getElementById(`source-${sourceId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    };

    const handleMemoryClick = (memoryId: string) => {
      setActiveMemoryId(memoryId);

      // Scroll to the memory in the Referenced Context section will be handled by ReferencedMemoriesSection
      // No need to scroll here since the component will handle it via useEffect
    };

    return (
      <div ref={ref} className="overflow-hidden">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ),
            p: ({ children, ...props }) => {
              // Check if this paragraph is inside a list item
              const isInListItem = (props.node?.position?.start?.column ?? 0) > 1;
              return <p className={cn("last:mb-0", isInListItem && "inline")}>{children}</p>;
            },
            table: ({ node, children }) => (
              <MarkdownTable node={isMdastTableNode(node) ? node : undefined}>{children}</MarkdownTable>
            ),
            pre: ({ children }) => {
              return (
                <pre className="bg-muted/80 border border-border rounded-md p-3 overflow-x-auto">
                  {children}
                </pre>
              );
            },
            code: ({ children, className }) => {
              const text = String(children);

              // Time marker code blocks
              if (text.startsWith(TIME_MARKER) && text.endsWith(TIME_MARKER)) {
                return <TimeMarker>{text.slice(1, -1)}</TimeMarker>;
              }

              // Step reference code blocks
              const stepReferenceMatch = text.match(/††step_([A-Za-z0-9_]+)††/);
              if (stepReferenceMatch && dagNodes && onSelectNode) {
                const nodeId = stepReferenceMatch[1];
                return (
                  <MarkdownDagNodeButton
                    nodeId={nodeId}
                    dagNodes={dagNodes}
                    selectedNodeId={selectedNodeId || null}
                    onSelectNode={onSelectNode}
                  />
                );
              }

              // Tool reference code blocks (MessageProcessor compatibility)
              const callReferenceMatch = text.match(/††(call_[A-Za-z0-9_/]+)††/);
              if (callReferenceMatch) {
                const callId = callReferenceMatch[1];
                const callSourceId = `tool_${callId}`;

                // If we have sources and this tool reference has a corresponding source, render it
                if (sources && sourceIdToIndex[callSourceId]) {
                  return (
                    <SourceReference
                      key={`tool-ref-${callSourceId}`}
                      sourceId={callSourceId}
                      index={sourceIdToIndex[callSourceId]}
                      isActive={currentActiveId === callId}
                      onClick={handleSourceClick}
                    />
                  );
                } else {
                  // Hide the tool reference if it's not in the sources
                  return null;
                }
              }

              // Tool reference code blocks (MessageProcessor compatibility)
              const tooluReferenceMatch = text.match(/††(toolu_[A-Za-z0-9_/]+)††/);
              if (tooluReferenceMatch) {
                const toolId = tooluReferenceMatch[1];
                const toolSourceId = `tool_${toolId}`;

                // If we have sources and this tool reference has a corresponding source, render it
                if (sources && sourceIdToIndex[toolSourceId]) {
                  return (
                    <SourceReference
                      key={`tool-ref-${toolSourceId}`}
                      sourceId={toolSourceId}
                      index={sourceIdToIndex[toolSourceId]}
                      isActive={currentActiveId === toolId}
                      onClick={handleSourceClick}
                    />
                  );
                } else {
                  // Hide the tool reference if it's not in the sources
                  return null;
                }
              }

              // Tool reference code blocks (MessageProcessor compatibility)
              const toolReferenceMatch = text.match(/††(tool_[A-Za-z0-9_/]+)††/);
              if (toolReferenceMatch) {
                const toolId = toolReferenceMatch[1];
                const toolSourceId = `tool_${toolId}`;

                // If we have sources and this tool reference has a corresponding source, render it
                if (sources && sourceIdToIndex[toolSourceId]) {
                  return (
                    <SourceReference
                      key={`tool-ref-${toolSourceId}`}
                      sourceId={toolSourceId}
                      index={sourceIdToIndex[toolSourceId]}
                      isActive={currentActiveId === toolId}
                      onClick={handleSourceClick}
                    />
                  );
                } else {
                  // Hide the tool reference if it's not in the sources
                  return null;
                }
              }

              // Artifact reference code blocks (tierzero_claude_agent compatibility)
              const artifactReferenceMatch = text.match(/††(artifact_[\w-]+\.json)††/);
              if (artifactReferenceMatch) {
                const artifactId = artifactReferenceMatch[1];
                const artifactSourceId = `tool_${artifactId}`;

                // If we have sources and this artifact reference has a corresponding source, render it
                if (sources && sourceIdToIndex[artifactSourceId]) {
                  return (
                    <SourceReference
                      key={`tool-ref-${artifactSourceId}`}
                      sourceId={artifactSourceId}
                      index={sourceIdToIndex[artifactSourceId]}
                      isActive={currentActiveId === artifactId}
                      onClick={handleSourceClick}
                    />
                  );
                } else {
                  // Hide the artifact reference if it's not in the sources
                  return null;
                }
              }

              // Source reference code blocks
              const sourceReferenceMatch = text.match(/††source_([A-Za-z0-9_]+)_([A-Za-z0-9_]+)††/);
              if (sourceReferenceMatch && dagNodes) {
                const nodeId = sourceReferenceMatch[1];
                const sourceId = sourceReferenceMatch[2];
                const fullSourceId = `${nodeId}_${sourceId}`;

                if (sourceIdToIndex[fullSourceId]) {
                  return (
                    <SourceReference
                      key={`source-ref-${fullSourceId}`}
                      sourceId={fullSourceId}
                      index={sourceIdToIndex[fullSourceId]}
                      isActive={currentActiveId === fullSourceId}
                      onClick={handleSourceClick}
                    />
                  );
                } else {
                  // Hide the source reference if it's not in the dagNodes. This is probably an LLM issue
                  return null;
                }
              }

              // Data source numeric references [1], [2], [3]
              const numericReferenceMatch = text.match(/^\[(\d+)\]$/);
              if (numericReferenceMatch) {
                const sourceNumber = parseInt(numericReferenceMatch[1], 10);
                // Find the source ID that matches this index number
                const sourceId = Object.keys(sourceIdToIndex).find(
                  id => sourceIdToIndex[id] === sourceNumber
                );

                if (sourceId) {
                  return (
                    <SourceReference
                      key={`numeric-ref-${sourceNumber}`}
                      sourceId={sourceId}
                      index={sourceNumber}
                      isActive={currentActiveId === sourceId}
                      onClick={handleSourceClick}
                    />
                  );
                }
                // If no matching source, just render as plain text
                return <code className="regular-code">{text}</code>;
              }

              // Memory letter references [a], [b], [c]
              const letterReferenceMatch = text.match(/^\[([a-z])\]$/);
              if (letterReferenceMatch && memoryAttributions && memoryAttributions.length > 0) {
                const letter = letterReferenceMatch[1];
                const letterIndex = letter.charCodeAt(0) - 97; // a->0, b->1, c->2

                // Check if we have a memory at this index
                if (letterIndex < memoryAttributions.length) {
                  const memory = memoryAttributions[letterIndex];
                  return (
                    <MemoryReference
                      key={`memory-ref-${letter}`}
                      memoryId={memory.memoryId}
                      letter={letter}
                      isActive={activeMemoryId === memory.memoryId}
                      onClick={handleMemoryClick}
                    />
                  );
                }
                // If no matching memory, just render as plain text
                return <code className="regular-code">{text}</code>;
              }

              // Regular code blocks
              if (timeOnly) {
                return <span>{children}</span>;
              }

              // Check if this is inside a pre block (multiline code) or has language info
              const isInPreBlock = className?.includes("language-") || text.includes("\n");
              const codeClass = isInPreBlock ? "text-code" : "regular-code";

              return <code className={codeClass}>{children}</code>;
            },
          }}
          className={cn(
            "prose-sm prose max-w-none whitespace-pre-wrap leading-[0px] break-words hyphens-auto",
            variant === "default" && "dark:prose-invert",
            "prose-ul:my-2 prose-ol:my-2",
            "prose-p:leading-relaxed prose-li:leading-snug",
            "prose-p:mb-0",
            // Fix nested list styles
            "[&_li>p]:mb-0",
            "[&_li>ul]:mt-0 [&_li>ol]:mt-0",
            "[&_ol]:list-decimal [&_ol]:pl-6",
            "[&_ul]:list-disc [&_ul]:pl-6",
            "[&_li]:my-0",
            // Fix whitespace handling in lists
            "[&_ol]:whitespace-normal [&_ul]:whitespace-normal",
            "[&_li]:whitespace-normal",
            variant === "default" && "[&_ul>li::marker]:text-foreground",
            // Code blocks
            "[&_code]:before:hidden [&_code]:after:hidden", // Hide the code block backticks
            "[&_pre]:before:hidden [&_pre]:after:hidden", // Hide any pre-block artifacts
            // Hide language labels in code blocks
            "[&_pre_code::before]:hidden [&_pre_code::after]:hidden",
            variant === "default"
              ? "prose-code:text-amber-700 dark:prose-code:text-amber-500 prose-code:font-normal"
              : "prose-code:font-bold",
            // Regular code blocks (inline)
            variant === "default"
              ? "[&_.regular-code]:border [&_.regular-code]:border-border [&_.regular-code]:bg-accent [&_.regular-code]:rounded-md [&_.regular-code]:px-1.5 [&_.regular-code]:py-0.5"
              : "",
            variant === "default" && "[&_.regular-code]:rounded [&_.regular-code]:px-1 [&_.regular-code]:py-0.25",
            variant === "default"
              ? "[&_.regular-code]:break-all [&_.regular-code]:whitespace-break-spaces prose-pre:break-words"
              : "prose-pre:break-words",
            // Text code blocks (multiline)
            variant === "default" ? "[&_.text-code]:break-all [&_.text-code]:whitespace-break-spaces" : "",
            // Fix long URL breaking in links
            "prose-a:break-all prose-a:overflow-wrap-anywhere",
            // Time marker code blocks
            "[&_span.time]:border-b [&_span.time]:border-dotted",
            "prose-pre:my-1",
            // Table styles (legacy, retained for printable variant fallback)
            "prose-table:border-collapse prose-table:w-full prose-table:my-1",
            "prose-thead:bg-card prose-thead:text-foreground",
            "prose-th:border prose-th:border-border prose-th:p-2 prose-th:text-left",
            "prose-td:border prose-td:border-border prose-td:p-2 prose-td:text-foreground",
            "[&_tbody_tr:hover_td]:bg-card",
            className,
          )}
        >
          {processedText}
        </ReactMarkdown>

        {showSourcesSection && allSources.length > 0 && (
          <SourcesSection
            sources={allSources}
            activeSourceId={currentActiveId}
            onSourceClick={handleSourceClick}
            forceInlineArtifacts={forceInlineArtifacts}
            isExpanded={sourcesExpanded}
            onExpandedChange={onSourcesExpandedChange}
          />
        )}

        {/* Referenced Context Section - Loaded Asynchronously */}
        <ReferencedMemoriesSectionAsync
          memoryAttributions={memoryAttributions}
          conversationId={conversationId}
          messageId={messageId}
          onEditMemory={onEditMemory}
          isV2={isV2}
          activeMemoryId={activeMemoryId}
          onMemoryClick={handleMemoryClick}
        />

        {((usage?.billingTokens != null && !hideUsage) || copyButton) && showSourcesSection && allSources.length > 0 && (
          <hr className="my-3 border-t border-border/50" />
        )}

        {((usage?.billingTokens != null && !hideUsage) || copyButton) && (
          <>
            {!(showSourcesSection && allSources.length > 0) && (
              <hr className="my-3 border-t border-border/50" />
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {copyButton && <div>{copyButton}</div>}
              {usage?.billingTokens != null && !hideUsage && (
                <div className="md:ml-auto inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted-foreground bg-muted/30">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Usage: </span>
                  <span className="font-mono text-amber-400">{(usage.billingTokens / 100000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span>credits</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  },
);

Markdown.displayName = "Markdown";

export { MarkdownTable } from "./markdown-table";
